import { Box, CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import DropdownSelect from '../../components/DropdownSelect';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { academicYears } from '../../constants/academicYears';
import { RootState } from '../../redux/store';
import { downloadReport } from '../../utils/reportUtils';
import { Title, PaperWrapper } from '../common/styled';
import { SearchInputWrapper, RoundedButton } from './styled';
import ConcessionsAndWaiversReportTable from './components/ConcessionsAndWaiversReportTable';
import { getConcessionsAndWaiversReport } from '../../apis/payments/concessionsAndWaiversReport';
import { transformConcessionsAndWaiversData } from './utils';
import { ConcessionsAndWaiversApiResponse } from './types';

const PER_PAGE = 100;

const ConcessionsAndWaiversReportPage = () => {
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const [academicYear, setAcademicYear] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const academicYearOptions = academicYears.map((y) => ({
    label: y.label,
    value: y.value
  }));

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleView = async () => {
    if (!academicYear) {
      showToast('Please select an academic year', SnackbarSeverityType.error);
      return;
    }
    setIsSearching(true);
    setHasData(false);
    setReportData([]);
    try {
      let page = 1;
      const allSchemes: any[] = [];

      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const response = (await getConcessionsAndWaiversReport(
          academicYear,
          page,
          PER_PAGE
        )) as unknown as ConcessionsAndWaiversApiResponse;

        allSchemes.push(...(response.data || []));
        if (!response.hasMore) break;
        page += 1;
      }

      const rows = transformConcessionsAndWaiversData(allSchemes);
      setReportData(rows);
      setHasData(true);
      showToast('Report loaded successfully', SnackbarSeverityType.success);
    } catch (error: any) {
      showToast(
        error.errors?.[0]?.message || 'Error fetching report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleDownload = () => {
    setIsDownloading(true);
    try {
      downloadReport(
        reportData,
        `${loggedInUser.school}-ConcessionsAndWaiversReport-${academicYear}`,
        'Concessions and Waivers Report',
        showToast
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box>
      <Title>Concessions and Waivers Report</Title>
      <PaperWrapper sx={{ minHeight: 'auto' }}>
        <SearchInputWrapper>
          <Box sx={{ flex: 1, maxWidth: 280 }}>
            <DropdownSelect
              label='Academic Year'
              placeholder='Select Academic Year'
              name='academicYear'
              value={academicYear}
              options={academicYearOptions}
              onChange={(e) => {
                setAcademicYear(e.target.value as string);
                setHasData(false);
              }}
              hasTopLabel
            />
          </Box>
          <RoundedButton onClick={handleView} disabled={isSearching}>
            {isSearching ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'View'
            )}
          </RoundedButton>
          <RoundedButton
            onClick={handleDownload}
            disabled={!hasData || isDownloading || isSearching}
          >
            {isDownloading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Download'
            )}
          </RoundedButton>
        </SearchInputWrapper>
      </PaperWrapper>
      {hasData && !isSearching && (
        <ConcessionsAndWaiversReportTable rows={reportData} />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={() => setToastOpen(false)}
      />
    </Box>
  );
};

export default ConcessionsAndWaiversReportPage;
